import { Injectable, ComponentRef, ApplicationRef, createComponent, EnvironmentInjector, Type, inject } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class DialogService {
  private applicationRef = inject(ApplicationRef);
  private injector = inject(EnvironmentInjector);
  private dialogComponentRef: ComponentRef<any> | null = null;
  private resolvePromise: ((result?: any) => void) | null = null;
  private sessionExpirationHandling = false;

  constructor() {}

  /**
   * Opens a dialog with the specified component
   * @param component The component to display in the dialog
   * @param data Optional data to pass to the component
   * @returns Promise that resolves when dialog is closed
   */
  open<T>(component: Type<T>, data?: any): Promise<any> {
    return new Promise((resolve) => {
      // Close any existing dialog
      this.close();

      // Store the resolve function
      this.resolvePromise = resolve;

      // Create the component
      this.dialogComponentRef = createComponent(component, {
        environmentInjector: this.injector
      });

      // Pass data to component if provided
      if (data && this.dialogComponentRef.instance) {
        Object.assign(this.dialogComponentRef.instance, data);
      }

      // Attach to application
      this.applicationRef.attachView(this.dialogComponentRef.hostView);

      // Get the DOM element
      const domElem = (this.dialogComponentRef.hostView as any).rootNodes[0] as HTMLElement;

      // Append to body
      document.body.appendChild(domElem);
    });
  }

  /**
   * Closes the currently open dialog
   * @param result Optional result to pass to the promise
   */
  close(result?: any): void {
    if (this.dialogComponentRef) {
      // Get the DOM element before destroying
      const domElem = (this.dialogComponentRef.hostView as any).rootNodes[0] as HTMLElement;
      
      // Remove from DOM
      if (domElem && domElem.parentNode) {
        domElem.parentNode.removeChild(domElem);
      }
      
      // Detach view and destroy component
      this.applicationRef.detachView(this.dialogComponentRef.hostView);
      this.dialogComponentRef.destroy();
      this.dialogComponentRef = null;
    }

    // Resolve the promise if it exists
    if (this.resolvePromise) {
      this.resolvePromise(result);
      this.resolvePromise = null;
    }
  }

  /**
   * Check if a dialog is currently open
   */
  isOpen(): boolean {
    return this.dialogComponentRef !== null;
  }

  /** Prevents duplicate session-expired handling when multiple API calls fail at once. */
  beginSessionExpiration(): boolean {
    if (this.sessionExpirationHandling || this.isOpen()) {
      return false;
    }
    this.sessionExpirationHandling = true;
    return true;
  }

  endSessionExpiration(): void {
    this.sessionExpirationHandling = false;
  }

  isSessionExpirationHandling(): boolean {
    return this.sessionExpirationHandling || this.isOpen();
  }
}

